import { Card, CardContent } from "../../ui/card";
import { formatCurrency } from "../../../utils/formatCurrency";
import type { FilteredMetrics } from "../hooks/useFilteredPerformance";
import { DollarSign, TrendingUp, Package, ListFilter } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../ui/accordion";
import { DataTable } from "../../ui/data-table/data-table";

interface FilteredKpiSummaryProps {
  filteredMetrics: FilteredMetrics;
  isLoading?: boolean;
}

export function FilteredKpiSummary({
  filteredMetrics,
  isLoading,
}: FilteredKpiSummaryProps) {
  const {
    totalRevenue,
    shopifyRevenue,
    wholesaleRevenue,
    itemCount,
    hasActiveFilters,
    matchingItems,
  } = filteredMetrics;

  // Calculate average if we have items
  const averageRevenue = itemCount > 0 ? totalRevenue / itemCount : 0;

  if (isLoading) {
    return (
      <Card className="mb-6 border-2 border-primary/30">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Loading metrics...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!hasActiveFilters) {
    return (
      <Card className="mb-6 border-2 border-muted bg-muted/20">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <ListFilter className="h-12 w-12 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">
              Select filters above to view a customized summary
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Filter by channel, category, gender, collection, color, or style
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Columns for the data table
  const columns = [
    {
      accessorKey: "dimension",
      header: "Dimension",
    },
    {
      accessorKey: "name",
      header: "Name",
    },
    {
      accessorKey: "shopify_revenue",
      header: "Shopify Revenue",
      isNumber: true,
      cell: (info: any) => formatCurrency(info.getValue()),
    },
    {
      accessorKey: "wholesale_revenue",
      header: "Wholesale Revenue",
      isNumber: true,
      cell: (info: any) => formatCurrency(info.getValue()),
    },
    {
      accessorKey: "total_revenue",
      header: "Total Revenue",
      isNumber: true,
      cell: (info: any) => formatCurrency(info.getValue()),
    },
  ];

  return (
    <Card className="mb-6 border-2 border-primary/30 shadow-lg">
      <CardContent className="pt-6">
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-semibold">Filtered Summary</h3>
            <div className="text-xs text-muted-foreground bg-amber-500/10 border border-amber-500/20 rounded-md px-3 py-1">
              Does not affect charts below
            </div>
          </div>

          {/* KPI Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Total Revenue */}
            <div className="bg-gradient-to-br from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/20 rounded-lg p-4 border border-primary/20">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-muted-foreground">
                  Total Revenue
                </p>
                <DollarSign className="h-5 w-5 text-primary" />
              </div>
              <p className="text-2xl font-bold">{formatCurrency(totalRevenue)}</p>
            </div>

            {/* Shopify Revenue */}
            <div className="bg-gradient-to-br from-blue-500/5 to-blue-500/10 dark:from-blue-500/10 dark:to-blue-500/20 rounded-lg p-4 border border-blue-500/20">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-muted-foreground">
                  Shopify Revenue
                </p>
                <TrendingUp className="h-5 w-5 text-blue-500" />
              </div>
              <p className="text-2xl font-bold">{formatCurrency(shopifyRevenue)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {totalRevenue > 0
                  ? `${((shopifyRevenue / totalRevenue) * 100).toFixed(1)}% of total`
                  : "0% of total"}
              </p>
            </div>

            {/* Wholesale Revenue */}
            <div className="bg-gradient-to-br from-green-500/5 to-green-500/10 dark:from-green-500/10 dark:to-green-500/20 rounded-lg p-4 border border-green-500/20">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-muted-foreground">
                  Wholesale Revenue
                </p>
                <Package className="h-5 w-5 text-green-500" />
              </div>
              <p className="text-2xl font-bold">
                {formatCurrency(wholesaleRevenue)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {totalRevenue > 0
                  ? `${((wholesaleRevenue / totalRevenue) * 100).toFixed(1)}% of total`
                  : "0% of total"}
              </p>
            </div>

            {/* Average Revenue */}
            <div className="bg-gradient-to-br from-purple-500/5 to-purple-500/10 dark:from-purple-500/10 dark:to-purple-500/20 rounded-lg p-4 border border-purple-500/20">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-muted-foreground">
                  Average Revenue
                </p>
                <TrendingUp className="h-5 w-5 text-purple-500" />
              </div>
              <p className="text-2xl font-bold">
                {formatCurrency(averageRevenue)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {itemCount} matching {itemCount === 1 ? "item" : "items"}
              </p>
            </div>
          </div>

          {/* Expandable Table */}
          {matchingItems.length > 0 && (
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="details" className="border-none">
                <AccordionTrigger className="hover:no-underline py-3 px-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors">
                  <span className="text-sm font-medium">
                    View Detailed Breakdown ({matchingItems.length}{" "}
                    {matchingItems.length === 1 ? "item" : "items"})
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pt-4">
                  <div className="rounded-md border">
                    <DataTable
                      columns={columns}
                      data={matchingItems}
                      searchColumn="name"
                      searchPlaceholder="Search items..."
                      showRowsPerPage={true}
                      initialPageSize={10}
                      showTotal={true}
                      totalColumns={["total_revenue"]}
                    />
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


